// contexts/ColorContext.jsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const ColorContext = createContext(null);

// Palettes fixes par profil VARK
const VARK_COLORS = {
  visual: '#7C3AED',    // violet
  auditory: '#3B82F6',   // bleu
  reading: '#10B981',    // vert
  kinesthetic: '#F59E0B' // orange
};

// Couleurs fonctionnelles fixes
const FUNCTIONAL_COLORS = {
  success: '#10B981',
  error: '#EF4444',
  warning: '#F59E0B',
  info: '#3B82F6'
};

const THEME_TYPES = {
  CLEAR: 'clear',
  DARK: 'dark',
  SYSTEM: 'system',
};

const invertHexColor = (hex, fallback = '#000000') => {
  const value = String(hex || '').trim();
  const safeHex = /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
  const r = 255 - parseInt(safeHex.slice(1, 3), 16);
  const g = 255 - parseInt(safeHex.slice(3, 5), 16);
  const b = 255 - parseInt(safeHex.slice(5, 7), 16);

  const toHex = (channel) => channel.toString(16).padStart(2, '0').toUpperCase();
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const darkenHexColor = (hex, amount = 0.06, fallback = '#000000') => {
  const value = String(hex || '').trim();
  const safeHex = /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
  const ratio = Math.min(Math.max(Number(amount) || 0, 0), 1);
  const darkenChannel = (channelHex) => Math.max(0, Math.round(parseInt(channelHex, 16) * (1 - ratio)));

  const r = darkenChannel(safeHex.slice(1, 3));
  const g = darkenChannel(safeHex.slice(3, 5));
  const b = darkenChannel(safeHex.slice(5, 7));

  const toHex = (channel) => channel.toString(16).padStart(2, '0').toUpperCase();
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const ColorProvider = ({ children }) => {
  const [theme_type, setTheme_type] = useState(THEME_TYPES.CLEAR);
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    return Boolean(window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  // Couleurs de fond
  const [bgPrimary, setBgPrimary] = useState('#111111');
  const [bgSecondary, setBgSecondary] = useState('#191919');
  const [bgTertiary, setBgTertiary] = useState('#1E1E1E'); // pour les cartes en relief

  // Couleurs de texte
  const [textPrimary, setTextPrimary] = useState('#F0F0F0');
  const [textSecondary, setTextSecondary] = useState('#BDBDBD');
  const [textTertiary, setTextTertiary] = useState('#9E9E9E'); // pour les labels secondaires

  // Profil VARK actif (dominant)
  const [activeVARK, setActiveVARK] = useState('visual'); // 'visual', 'auditory', 'reading', 'kinesthetic'

  // La couleur d'accent active est calculée en fonction du profil
  const activeAccent = VARK_COLORS[activeVARK];

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const updatePreference = () => setSystemPrefersDark(Boolean(mediaQuery.matches));
    updatePreference();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updatePreference);
      return () => mediaQuery.removeEventListener('change', updatePreference);
    }

    mediaQuery.addListener(updatePreference);
    return () => mediaQuery.removeListener(updatePreference);
  }, []);

  const setThemeType = useCallback((nextThemeType) => {
    const normalized = String(nextThemeType || '').toLowerCase();
    if (normalized === THEME_TYPES.CLEAR || normalized === THEME_TYPES.DARK || normalized === THEME_TYPES.SYSTEM) {
      setTheme_type(normalized);
    }
  }, []);

  const resolvedThemeType = theme_type === THEME_TYPES.SYSTEM
    ? (systemPrefersDark ? THEME_TYPES.DARK : THEME_TYPES.CLEAR)
    : theme_type;

  const useClearTheme = resolvedThemeType === THEME_TYPES.CLEAR;

  const effectiveBgPrimary = useClearTheme ? '#F9F9F9' : bgPrimary;
  const effectiveBgSecondary = useClearTheme ? '#F0F0F0' : bgSecondary;
  const effectiveBgTertiary = useClearTheme ? '#FFFFFF' : bgTertiary;

  const effectiveTextPrimary = useClearTheme ? invertHexColor(textPrimary, '#F0F0F0') : textPrimary;
  const effectiveTextSecondary = useClearTheme ? invertHexColor(textSecondary, '#BDBDBD') : textSecondary;
  const effectiveTextTertiary = useClearTheme ? invertHexColor(textTertiary, '#9E9E9E') : textTertiary;

  // Fonction pour changer le profil (sera appelée après le questionnaire)
  const changeVARKProfile = (profile) => {
    if (VARK_COLORS[profile]) {
      setActiveVARK(profile);
    }
  };

  const value = useMemo(() => ({
    // Theme
    theme_type,
    resolvedThemeType,
    setTheme_type: setThemeType,
    // Fond
    bgPrimary: effectiveBgPrimary,
    bgSecondary: effectiveBgSecondary,
    bgTertiary: effectiveBgTertiary,
    setBgPrimary,
    setBgSecondary,
    setBgTertiary,
    // Texte
    textPrimary: effectiveTextPrimary,
    textSecondary: effectiveTextSecondary,
    textTertiary: effectiveTextTertiary,
    setTextPrimary,
    setTextSecondary,
    setTextTertiary,
    // VARK
    activeVARK,
    activeAccent,
    changeVARKProfile,
    // Palettes fixes (si besoin de les exposer)
    varkColors: VARK_COLORS,
    functionalColors: FUNCTIONAL_COLORS,
  }), [
    theme_type,
    resolvedThemeType,
    effectiveBgPrimary, effectiveBgSecondary, effectiveBgTertiary,
    effectiveTextPrimary, effectiveTextSecondary, effectiveTextTertiary,
    activeVARK, activeAccent,
    setThemeType,
  ]);

  return (
    <ColorContext.Provider value={value}>
      {children}
    </ColorContext.Provider>
  );
};

const useColorContext = () => {
  const context = useContext(ColorContext);
  if (!context) {
    throw new Error('useColorContext must be used within a ColorProvider');
  }
  return context;
};

export {
  ColorContext,
  ColorProvider,
  useColorContext,
  VARK_COLORS,
  FUNCTIONAL_COLORS,
};